import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Sparkles, 
  Gem, 
  Star, 
  Clock,
  Users,
  ChevronRight,
  Hand,
  Scissors,
  Eye,
  Droplets,
  Heart
} from "lucide-react";
import InquiryForm from "@/components/InquiryForm";

const heroImage = "https://images.pexels.com/photos/11896310/pexels-photo-11896310.jpeg";
const nailImage = "https://images.pexels.com/photos/3997391/pexels-photo-3997391.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";
const lashImage = "https://images.pexels.com/photos/3985329/pexels-photo-3985329.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";
const hairImage = "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";

const courses = [
  {
    id: "beginner_facial",
    title: "Beginner Facial Course",
    price: 45000,
    kitPrice: 10000,
    kitNote: "Free Kit",
    icon: Sparkles,
    description: "Foundation facial training covering skin analysis, massage techniques, and hands-on practice with common facial treatments.",
    curriculum: ["Skin Analysis", "Facial Massage", "Facial Treatment", "Hygiene and Safety", "Client Consultation", "Contraindication", "Hands-on Practice", "Acne Facial", "Anti-Aging Facial", "Skin Brightening Facial"]
  },
  {
    id: "advanced_facial",
    title: "Advanced Facial Course",
    price: 55000,
    kitPrice: 15000,
    icon: Sparkles,
    description: "Advanced facial techniques including chemical peels, LED therapy, nano needling, and professional sculpting massage. Includes social media marketing training.",
    curriculum: ["Identifying Skin Conditions & Disorders", "Professional Exfoliating Methods", "Enzyme Exfoliation", "Safe Use of Mild Chemical Peels", "LED Therapy Facial", "Nano Needling Facial", "Mild Chemical Facial", "Lymphatic Drainage Facial Massage", "Sculpting Massage", "Social Media Marketing"]
  },
  {
    id: "lip_care",
    title: "Lip Care Product Making Course",
    price: 60000,
    kitPrice: 20000,
    icon: Heart,
    description: "Learn to create professional lip care products from scratch — lip gloss, lip balm, and lip scrub. Includes product design, branding, and marketing tips to start your own business.",
    curriculum: ["Lip Care Basics", "Ingredients and Tools", "Lip Gloss Making", "Lip Balm Making", "Lip Scrub Making", "Product Design and Branding", "Marketing Tips"]
  },
  {
    id: "beginners_nail",
    title: "Beginners Nail Course",
    price: 40000,
    kitPrice: 15000,
    icon: Hand,
    description: "Foundation nail training covering shaping, gel polish, nail art, and proper hygiene. Perfect for beginners starting their nail career.",
    curriculum: ["Nail Shaping", "Nail Disease and Disorders", "Hygiene and Sanitation", "Nail Care", "Gel Polish Application", "Nail Tips Application", "Nail Art", "E-Filing", "Removal of Acrylic", "Client Aftercare"]
  },
  {
    id: "gelx_builder",
    title: "Gel-X & Builder Gel Nail Course",
    price: 50000,
    kitPrice: 15000,
    icon: Gem,
    description: "Level 2 nail course covering Gel-X application, builder gel techniques, advanced shaping, and nail art. Includes retention tips and refill techniques.",
    curriculum: ["Sanitation & Hygiene", "Correct Tip Sizing", "Gel-X Application", "Shaping and Refining", "Nail Art", "Gel-X Refill & Removal", "Retention Tips"]
  },
  {
    id: "acrylic",
    title: "Acrylic Nails Course",
    price: 50000,
    kitPrice: 18000,
    icon: Gem,
    description: "Professional acrylic nail application and sculpting techniques.",
    curriculum: ["Acrylic Application", "Nail Sculpting", "Fill-ins", "Nail Shaping", "Design Techniques", "Maintenance"]
  },
  {
    id: "advanced_nail",
    title: "Advanced Nail Course",
    price: 50000,
    kitPrice: 18000,
    icon: Gem,
    description: "Take your nail skills to the next level with advanced acrylic techniques including encapsulation, ombre, 3D art, and French application.",
    curriculum: ["Acrylic Encapsulate", "Acrylic Ombre", "Acrylic 3D Flower", "Acrylic Application on Toes", "Acrylic French", "Nail Brush Art"]
  },
  {
    id: "lash",
    title: "Lash Extension Course",
    price: 60000,
    kitPrice: 20000,
    icon: Eye,
    description: "Learn professional lash extension application for stunning results.",
    curriculum: ["Classic Lashes", "Volume Lashes", "Lash Mapping", "Adhesive Techniques", "Aftercare", "Removal"]
  },
  {
    id: "lash_lift",
    title: "Lash Lift Training Course",
    price: 45000,
    kitPrice: null,
    icon: Eye,
    description: "Learn how to achieve perfectly lifted, natural-looking lashes.",
    curriculum: ["Lash Lift Theory & Safety", "Products & Tools", "Step-by-Step Procedure", "Lash Tinting", "Hands-on Training", "Certificate Included"]
  },
  {
    id: "waxing",
    title: "Body Waxing Course",
    price: 60000,
    kitPrice: 20000,
    icon: Droplets,
    description: "Complete body waxing techniques for all areas.",
    curriculum: ["Facial Waxing", "Leg Waxing", "Underarm Waxing", "Brazilian", "Safety & Hygiene", "Aftercare"]
  },
  {
    id: "hair",
    title: "Hair Services Course",
    price: 55000,
    kitPrice: 35000,
    icon: Scissors,
    description: "Comprehensive hair services training covering bleaching, coloring, styling, conditioning, and scalp analysis.",
    curriculum: ["Bleaching", "Coloring", "Silk Press", "Deep Conditioning", "Hair Straightening", "Scalp and Hair Analysis", "Curling Technique"]
  },
  {
    id: "advanced_hair",
    title: "Advanced Hair Course",
    price: 60000,
    kitPrice: 40000,
    icon: Scissors,
    description: "4-week advanced course covering Botox, Keratin, hair extensions, and highlights. Study guide and certificate included. Payment plan available.",
    curriculum: ["Botox Application & Treatment", "Keratin Application & Treatment", "Tape-in Extensions & Repair", "K-Tips Extension Application", "U-Tips Extension Application", "How to Make K-Tips Extensions", "Hair Extensions Removal", "Hair Highlights"]
  },
  {
    id: "brow_training",
    title: "Brow Tinting, Mapping, Laminating & Waxing",
    price: 40000,
    kitPrice: 20000,
    icon: Eye,
    description: "Professional brow training course. Free training kit included — students keep their products to continue practising after the course.",
    curriculum: ["Brow Tinting", "Brow Mapping", "Brow Laminating", "Brow Waxing", "Professional Brow Kit", "Certificate Included"]
  },
  {
    id: "wig_masterclass",
    title: "Wig Master Class",
    price: 80000,
    kitPrice: 20000,
    icon: Scissors,
    description: "Hands-on wig course perfect for beginners and anyone looking to learn professionally. Tools provided, certificate included. Free kit — deposit required to secure your spot.",
    curriculum: ["Wig Making (Full Construction)", "Wig Installation (Glue & Glueless)", "Coloring & Bleaching", "Customization (Plucking, Tinting, Styling)", "Wig Revamping", "Certificate Included"]
  },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const CoursesPage = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      setSelectedCourse(hash);
      setTimeout(() => {
        const el = document.getElementById(`course-${hash}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 500);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <div data-testid="courses-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Professional Training
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Start Your<br />
              <span className="gold-gradient">Beauty Career</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Transform your passion into a profession. Our comprehensive courses 
              provide hands-on training to help you launch your own beauty business.
              Limited spaces available - registration now open for April 2025.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Course Cards */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]" data-testid="courses-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <AnimatedSection key={course.id}>
                <div 
                  id={`course-${course.id}`}
                  className={`bento-card p-8 h-full cursor-pointer transition-all duration-500 ${
                    selectedCourse === course.id ? 'border-[#C5A059] ring-1 ring-[#C5A059]/30' : ''
                  }`}
                  onClick={() => setSelectedCourse(selectedCourse === course.id ? null : course.id)}
                  data-testid={`course-card-${course.id}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 border border-[#C5A059] flex items-center justify-center">
                      <course.icon className="w-6 h-6 text-[#C5A059]" />
                    </div>
                    <div className="text-right">
                      <span className="font-heading text-2xl text-[#C5A059]">${course.price.toLocaleString()}</span>
                      <span className="font-body text-xs text-[#A3A3A3] block">GYD</span>
                    </div>
                  </div>
                  
                  <h3 className="font-heading text-xl text-white mb-3">{course.title}</h3>
                  <p className="font-body text-sm text-[#A3A3A3] mb-4">{course.description}</p>
                  
                  {course.kitPrice ? (
                    <div className="flex items-center gap-4 text-[#A3A3A3] text-sm mb-4">
                      <span>Kit: ${course.kitPrice.toLocaleString()} GYD{course.kitNote ? ` (${course.kitNote})` : ""}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 text-[#A3A3A3] text-sm mb-4">
                      <span>Kit: Included</span>
                    </div>
                  )}

                  {selectedCourse === course.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="border-t border-[#262626] pt-4 mt-4"
                    >
                      <h4 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-3">
                        What You'll Learn
                      </h4>
                      <ul className="space-y-2">
                        {course.curriculum.map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                            <ChevronRight size={12} className="text-[#C5A059]" />
                            {item}
                          </li>
                        ))}
                      </ul>

                      <div className="flex gap-3 mt-6" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => document.getElementById('course-registration')?.scrollIntoView({ behavior: 'smooth' })}
                          data-testid={`course-register-${course.id}`}
                          className="flex-1 bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase py-3 text-center hover:brightness-110 transition-all"
                        >
                          Register Now
                        </button>
                        <Link
                          to={`/book`}
                          data-testid={`course-book-${course.id}`}
                          className="flex-1 border border-[#C5A059] text-[#C5A059] font-semibold text-xs tracking-[0.15em] uppercase py-3 text-center hover:bg-[#C5A059]/10 transition-all"
                        >
                          Book Now
                        </Link>
                      </div>
                    </motion.div>
                  )}

                  <div className="mt-4 pt-4 border-t border-[#262626]">
                    <span className="text-[#C5A059] text-sm">
                      {selectedCourse === course.id ? 'Click to collapse' : 'Click to see curriculum & book'}
                    </span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Train With Us */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Why Choose Us
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Learn From Experience
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">
                  Our training programs are designed to give you real-world skills 
                  you can use immediately. We focus on hands-on practice so you 
                  graduate confident and ready to start your own business.
                </p>
                <div className="space-y-4">
                  {[
                    "Hands-on practical training",
                    "Professional kit included",
                    "Small class sizes for personalized attention",
                    "Certificate upon completion",
                    "Ongoing support after graduation",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#C5A059]" />
                      <span className="font-body text-[#A3A3A3]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img src={nailImage} alt="Nail training" className="w-full h-48 object-cover" />
                <img src={lashImage} alt="Lash training" className="w-full h-48 object-cover" />
                <img src={hairImage} alt="Hair training" className="w-full h-48 object-cover col-span-2" />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Registration Section */}
      <section id="course-registration" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="course-registration">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Limited Spaces
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Register for Training
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">
                  Ready to start your beauty career? Select your course and submit 
                  your inquiry. We'll contact you within 24 hours to discuss your 
                  goals and secure your spot.
                </p>

                <div className="bento-card p-6">
                  <h4 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                    Available Courses
                  </h4>
                  <div className="space-y-3">
                    {courses.map((course) => (
                      <div key={course.id} className="flex justify-between items-center text-sm">
                        <span className="text-[#A3A3A3]">{course.title}</span>
                        <span className="text-[#C5A059]">${course.price.toLocaleString()} GYD</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <InquiryForm inquiryType="training" />
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;
